import type {
  Activity,
  ActivityContact as GqlActivityContact,
  AdCampaign,
  AdPlatformMapping,
  Appointment,
  AppointmentType,
  Availability,
  Call,
  CallPerson,
  CdtCodeMapping,
  ChannelMappingV2,
  Contact,
  ContactPipeline,
  ConversionLogItem,
  CustomPage,
  CustomPageFilter as GqlCustomPageFilter,
  FeatureToggle,
  FormFieldMapping,
  Import,
  Location,
  Message,
  Notification,
  Patient,
  Pipeline,
  PipelineStage,
  Procedure,
  Provider,
  ResourceSchedule,
  Role,
  Scheduler,
  Scorecard,
  Staff as GqlStaff,
  StaffSummary as GqlStaffSummary,
  TagColor,
  Task,
  Transaction,
  TreatmentPlan as GqlTreatmentPlan,
  User,
  UtmAlias,
  Workspace,
  WorkspaceInvite,
} from 'app/generated/graphql'

// TODO: AiAgentConfiguration was removed from the generated schema.
// Restore when the API re-exposes this type (TRK-5213 parity follow-up).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AiAgentConfiguration = any

export type { AiAgentConfiguration as AIAgentConfiguration }

export type {
  Activity,
  AdCampaign,
  AdPlatformMapping,
  Appointment,
  AppointmentType,
  Availability,
  Call,
  CdtCodeMapping,
  ChannelMappingV2,
  Contact,
  ContactPipeline,
  CustomPage,
  FeatureToggle,
  FormFieldMapping,
  Import,
  Location,
  Message,
  Notification,
  Patient,
  Pipeline,
  Provider,
  ResourceSchedule,
  Role,
  Scheduler,
  Scorecard,
  TagColor,
  Task,
  Transaction,
  User,
  UtmAlias,
  Workspace,
  WorkspaceInvite,
} from 'app/generated/graphql'

export type Entity<T> = Partial<T> & { id?: string }
export type IndexProperties<T> = Entity<T> & {
  version?: number
  createdDate?: Date | string
  updatedDate?: Date | string
}

export type ActivityDataValue = string | number | boolean | Date | null | undefined | { from?: string; to?: string }
export type ActivityData = Record<string, ActivityDataValue> & {
  closedLostReason?: string | { from?: string; to?: string }
  dueDate?: string | Date
  status?: string
}
export type ActivityJson = Entity<Omit<Activity, 'contacts' | 'data'>> & {
  contacts?: ActivityContact[]
  data?: ActivityData
  gclid?: string
  fbclid?: string
  referringUrl?: string
  email?: string
  nameFromActivity?: string
}
export type ActivityType = ActivityJson
export type ActivityContact = Entity<Omit<GqlActivityContact, '__typename'>> & {
  __typename?: string
  id: string
  name?: string
  phone?: string
  matchTypes?: string[]
  matchDate?: string
  nameFromActivity?: string
  nameSource?: string
  phoneFromActivity?: string
  channel?: string
  peopleIndex?: number
  locationId?: string
  lifecycleStage?: string
}
export type AdCampaignJson = Entity<AdCampaign>
export type AdCampaignType = AdCampaignJson
export type AdPlatformMappingJson = Entity<AdPlatformMapping>
export type AdPlatformMappingType = AdPlatformMappingJson
export type AIAgentConfigurationJson = Entity<AiAgentConfiguration>
export type AIAgentConfigurationType = AIAgentConfigurationJson
export type AppointmentJson = Entity<Appointment>
export type AppointmentTypeJson = Entity<AppointmentType> & {
  generalBuffer?: number
  morningBuffer?: number
  morningBufferLookahead?: number
}
export type AppointmentTypeType = AppointmentTypeJson
export type AvailabilityType = Entity<Omit<Availability, 'timeSlots'>> & { timeSlots?: OpenHours }
export type CallJson = Entity<Call>
export type CallType = CallJson
export type CallDataJson = {
  id?: string
  data?: Record<string, unknown>
}
export type CdtCodeMappingType = Entity<CdtCodeMapping>
export type ChannelMappingV2Json = Entity<ChannelMappingV2>
export type ChannelMappingV2Type = ChannelMappingV2Json
export type ContactJson = Entity<Omit<Contact, '__typename'>> & {
  __typename?: string
  nameSource?: string
  matchTypes?: string[]
}
export type ContactType = ContactJson
export type ContactPipelineJson = Entity<Omit<ContactPipeline, 'stage'>> & { stage?: string }
export type ConversionLog = Entity<ConversionLogItem>
export type ConversionType =
  | 'QUALIFIED_LEAD_GENERATED'
  | 'NEW_PATIENT_APPOINTMENT_SCHEDULED'
  | 'NEW_PATIENT_GENERATED'
  | 'ADDITIONAL_TREATMENT_REVENUE'
export const CONVERSION_TYPES: ConversionType[] = [
  'QUALIFIED_LEAD_GENERATED',
  'NEW_PATIENT_APPOINTMENT_SCHEDULED',
  'NEW_PATIENT_GENERATED',
  'ADDITIONAL_TREATMENT_REVENUE',
]
export type CustomPageFilter = GqlCustomPageFilter
export type CustomPageJson = Entity<Omit<CustomPage, 'filters'>> & { filters?: CustomPageFilter[] }
export type CustomPageType = CustomPageJson
export type CustomPages = 'contacts' | 'activities' | 'tasks' | 'treatmentPlans' | 'appointments'
export type FeatureToggleJson = Entity<FeatureToggle>
export type FileImportJson = {
  id?: string
  fileName?: string
}
export type FollowUpJson = {
  id?: string
  orgId?: string
  workspaceId?: string
  locationId?: string
  appointmentDate?: string
  followUpDate?: string
  isConnected?: boolean
  isScheduled?: boolean
  description?: string
  owner?: { id?: string; name?: string }
}
export type FollowUpType = FollowUpJson
export type FormJson = {
  id?: string
  orgId?: string
  workspaceId?: string
  locationId?: string
  patientName?: string
  phone?: string
  email?: string
  treatment?: string
  requestedAppointmentDate?: string | Date
  companyName?: string
  source?: string
  status?: string
  submissionDate?: string | Date
  title?: string
  gclid?: string
  channel?: string
  referralURL?: string
  landingPageURL?: string
  answers?: Record<string, string | number | boolean | null | undefined>
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
  utmKeywords?: string
}
export type Form = FormJson
export type FormFieldMappingJson = Entity<FormFieldMapping>
export type ImportJson = Entity<Import>
export type ImportType = ImportJson
export type LocationJson = Entity<Omit<Location, 'openHours'>> & {
  openHours?: OpenHours
  pmsIntegrated?: boolean
}
export type LocationType = LocationJson
export type LocationProductType = 'Scheduler' | 'AI Agent' | 'SMS Agent' | 'CRM' | 'Call Processing' | 'Inbox' | string
export type MessageJson = Entity<Message>
export type MessageType = MessageJson
export type NewPatientCalculationRule = 'ADA' | 'all-procedures' | 'new-patient-codes'
export type NotificationJson = Entity<Notification>
export type NotificationType = NotificationJson
export type NotificationTypes = string
export type Organization = Record<string, unknown> & { id?: string; name?: string }
export type PatientJson = Entity<Patient> & {
  groupNumber?: string
  pmsPatientId?: string
  patientCreatedDate?: string | Date
}
export type PatientType = PatientJson
export type Permission = string
export type PersonSummary = Entity<CallPerson>
export type PipelineType = Entity<Omit<Pipeline, 'stages'>> & { stages?: StageType[] }
export type ProviderType = Entity<Provider>
export type ResourceScheduleJson = Entity<ResourceSchedule>
export type RoleJson = Entity<Role>
export type ScorecardSort = { desc: boolean; id: string }
export type ScorecardJson = Entity<Omit<Scorecard, 'columnVisibility' | 'sort'>> & {
  columnVisibility?: Record<string, boolean>
  sort?: ScorecardSort[]
}
export type SchedulerJson = Entity<Scheduler>
export type SchedulingConfigurationJson = {
  id?: string
  orgId?: string
  workspaceId?: string
  locationId?: string
  customQuestions?: CustomQuestionType[]
  emailTemplates?: { id: string; subject: string; body: string }[]
  leadOverrideEnabled?: boolean
  leadOverrideConfig?: LeadOverrideConfigType
  hasLogo?: boolean
  trackingPhoneNumber?: string
  allowDoubleBooking?: boolean
  doubleBookingCount?: number
  insurancesAccepted?: string[]
  insurancesRejected?: string[]
  isStateInsuranceAccepted?: boolean
  notifyInOutNetwork?: boolean
  granularity?: number
}
export type SchedulingConfigurationType = SchedulingConfigurationJson
export type StaffJson = Entity<GqlStaff>
export type StaffType = StaffJson
export type StaffSummary = GqlStaffSummary
export type StaffOptionValue = StaffSummary & { email?: string; phone?: string }
export type TagColorJson = Entity<TagColor>
export type TagColorType = TagColorJson
export type TaskJson = Entity<Task>
export type TaskType = TaskJson
export type TransactionJson = Entity<Transaction> & {
  sikkaOfficeId?: string
  sikkaPracticeId?: string
  providerId?: string
  guarantorId?: string
  transactionSrNo?: string
  note?: string
}
export type TransactionType = TransactionJson
export type TreatmentPlanJson = Entity<GqlTreatmentPlan>
export type ProcedureJson = Entity<Procedure>
export type UserJson = Entity<User>
export type UserType = UserJson
export type UserSetting = Record<string, unknown>
export type UtmAliasJson = Entity<UtmAlias>
export type VerificationType = { status?: string; details?: string }
export type WorkspaceJson = Entity<Workspace>
export type WorkspaceType = WorkspaceJson
export type WorkspaceInviteJson = Entity<WorkspaceInvite>
export type Ad = { type: string }
export type BudgetType = {
  id?: string
  orgId?: string
  workspaceId?: string
  locationId?: string
  yearMonth?: string
  channel?: string
  amount?: number
}
export type CampaignJson = AdCampaignJson
export type StageType = Omit<PipelineStage, '__typename' | 'order' | 'color'> & {
  id?: string
  color?: string
  order?: number
}
export type LeadOverrideConfigType = {
  amount: number
  unit: 'days' | 'weeks'
}

export type Hours = { from: string; to: string }
export type OpenDay = {
  hours: Hours[]
  open: boolean
}
export type OpenHours = Partial<
  Record<'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday', OpenDay>
>
export type CustomQuestionType = {
  id: string
  fieldName: string
  required: boolean
  fieldType: 'one-line' | 'multiple-lines' | 'checkbox'
  enabled: boolean
  description?: string
  marketingConsent?: boolean
  optIn?: boolean
}

export const LOST_REASONS = [
  'Insurance: State / HMO',
  'Insurance: Out of Network',
  'Schedule Availability',
  'Pricing Too High',
  'Unable to Contact',
  'Service Not Offered',
  'Office Too Far',
  'Scheduled with Competitor',
  'Other',
  'Closed Lost: Other',
] as const

export function activityContacts(...contacts: Array<ContactType | undefined | null>): ActivityContact[] {
  return contacts.filter(Boolean).map((contact) => ({
    id: contact!.id!,
    name: contact!.name,
    phone: contact!.cellPhone || contact!.homePhone || contact!.workPhone,
    lifecycleStage: contact!.lifecycleStage,
    locationId: contact!.locationId,
  }))
}
